import React from 'react';
import { SITE, canonical, organisationSchema, webSiteSchema } from '../lib/seo';
interface SeoProps {
  /** The page title. The site name is appended, so do not include it. */
  title: string;
  /** One sentence stating what the page gives the reader. Aim for 140 to 160 characters. */
  description: string;
  /** The route, with a leading slash. Used for the canonical and Open Graph URL. */
  path: string;
  /** Absolute URL of the share image. Falls back to the brand mark. */
  image?: string;
  /** 'article' for insights, otherwise 'website'. */
  type?: 'website' | 'article';
  /** Structured data nodes. They are combined into one @graph with the site entity. */
  jsonLd?: object[];
  /** Keeps a page out of the index. Used for the portal and the utility routes. */
  noIndex?: boolean;
  /** Publication and author details, for article pages only. */
  article?: {
    publishedAt: string;
    author: string;
    section?: string;
    tags?: string[];
  };
}

/** Upsert a meta tag by name or property, marking it so it can be reconciled. */
function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}
function removeMeta(attr: 'name' | 'property', key: string) {
  document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
}

/**
 * Per page metadata for search and generative engines.
 *
 * This runs on the client, which is correct for the preview but not sufficient
 * for launch: an answer engine that does not execute JavaScript will read the
 * static shell. When this code moves into the Next.js project, each page's
 * `generateMetadata` export should be populated from the same strings, and this
 * component can then be deleted. See docs/backend-integration.md.
 */
export function Seo({
  title,
  description,
  path,
  image = SITE.shareImage,
  type = 'website',
  jsonLd = [],
  noIndex = false,
  article
}: SeoProps) {
  const url = canonical(path);
  const fullTitle = title.includes(SITE.name) ? title : `${title} | ${SITE.name}`;
  React.useEffect(() => {
    document.title = fullTitle;
    document.documentElement.lang = 'en-GB';
    setMeta('name', 'description', description);
    setMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1');

    // Canonical
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = url;

    // Open Graph
    setMeta('property', 'og:site_name', SITE.name);
    setMeta('property', 'og:locale', SITE.locale);
    setMeta('property', 'og:type', type);
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', image);

    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', image);
    if (article) {
      setMeta('property', 'article:published_time', article.publishedAt);
      setMeta('property', 'article:author', article.author);
      if (article.section) setMeta('property', 'article:section', article.section);
    } else {
      removeMeta('property', 'article:published_time');
      removeMeta('property', 'article:author');
      removeMeta('property', 'article:section');
    }

    // Structured data, as a single graph so the nodes can reference each other.
    const existing = document.getElementById('seo-page-schema');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'seo-page-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      // The organisation and the site are carried on every page, so an answer
      // engine can resolve the entity from whichever page it lands on.
      '@graph': [organisationSchema(), webSiteSchema(), ...jsonLd]
    });
    document.head.appendChild(script);
    // The graph is stringified, so comparing the serialised form keeps this
    // effect from re-running on every render when the object identity changes.
  }, [fullTitle, description, url, image, type, noIndex, JSON.stringify(jsonLd), JSON.stringify(article)]);
  return null;
}